<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { Check, Clock, Download, FileText } from 'lucide-vue-next'
import { formatDateTime, type ChecklistItemResult } from '@ck/domain'
import { CkAlert, CkButton, CkTable } from '@ck/ui'
import { accountsApi, checklistsApi, mediaApi, reportsApi, workOrdersApi } from '@ck/api-client'
import { identity } from '@ck/auth'
import { useActiveWorkOrder } from '../composables/useActiveWorkOrder'
import PortalHeader from '../components/PortalHeader.vue'

const customerName = ref(identity().name ?? '')
const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

onMounted(async () => {
  try {
    const me = await accountsApi.me()
    if (me.name) customerName.value = me.name
  } catch {
    // mantém o nome vazio quando /accounts/me não está disponível (ex.: authRequired=false)
  }
})

const { workOrderQuery, activeWorkOrder, detailQuery } = useActiveWorkOrder()
const conclusao = computed(() => detailQuery.data.value?.stages.find((s) => s.code === 'delivery' && s.status === 'completed')?.completedAt)

const checklistQuery = useQuery(computed(() => ({
  queryKey: ['checklists', activeWorkOrder.value?.id],
  queryFn: () => checklistsApi.get(activeWorkOrder.value!.id),
  enabled: !!activeWorkOrder.value
})))
function inspectionCounts(items: ChecklistItemResult[]) {
  return {
    ok: items.filter((i) => i.status === 'ok').length,
    warn: items.filter((i) => i.status === 'attention').length,
    fail: items.filter((i) => i.status === 'rejected').length
  }
}
const inspectionColumns = [
  { key: 'stage', label: 'Etapa', width: '1fr' },
  { key: 'ok', label: 'OK', width: '70px', align: 'center' as const },
  { key: 'warn', label: 'Atenção', width: '80px', align: 'center' as const },
  { key: 'fail', label: 'Reprovado', width: '90px', align: 'center' as const }
]

const mediaQuery = useQuery(computed(() => ({
  queryKey: ['media', activeWorkOrder.value?.id],
  queryFn: () => mediaApi.list(activeWorkOrder.value!.id),
  enabled: !!activeWorkOrder.value
})))
const mediaUrls = ref<Record<string, string>>({})
const photoBadge: Record<string, string> = { before: 'ANTES', during: 'DURANTE', after: 'DEPOIS' }
watch(() => mediaQuery.data.value, async (assets, _previous, onCleanup) => {
  const urls: Record<string, string> = {}
  for (const asset of assets ?? []) {
    try { urls[asset.id] = URL.createObjectURL(await mediaApi.fetchFile(asset.id)) } catch { /* ignora falha ao baixar uma foto específica */ }
  }
  mediaUrls.value = urls
  onCleanup(() => Object.values(urls).forEach((url) => URL.revokeObjectURL(url)))
}, { immediate: true })

const partsTotal = computed(() => (detailQuery.data.value?.parts ?? []).reduce((sum, p) => sum + p.quantity * p.unitPrice, 0))
const laborTotal = computed(() => (detailQuery.data.value?.labor ?? []).reduce((sum, l) => sum + l.hours * l.hourlyRate, 0))

const reportsQuery = useQuery(computed(() => ({
  queryKey: ['reports', activeWorkOrder.value?.id],
  queryFn: () => reportsApi.listByWorkOrder(activeWorkOrder.value!.id),
  enabled: !!activeWorkOrder.value
})))
const latestReport = computed(() => reportsQuery.data.value?.[0])
const reportDetailQuery = useQuery(computed(() => ({
  queryKey: ['report-detail', latestReport.value?.id],
  queryFn: () => reportsApi.get(latestReport.value!.id),
  enabled: !!latestReport.value
})))
const approval = computed(() => reportDetailQuery.data.value?.approvals[0])
const accepted = computed(() => !!approval.value)
const approving = ref(false)
const approveError = ref(false)
async function approveReport() {
  if (!latestReport.value) return
  approving.value = true
  approveError.value = false
  try {
    await workOrdersApi.approveReport(latestReport.value.id)
    await reportDetailQuery.refetch()
  } catch {
    approveError.value = true
  } finally {
    approving.value = false
  }
}

const downloading = ref(false)
async function downloadPdf() {
  if (!latestReport.value) return
  downloading.value = true
  try {
    const blob = await reportsApi.download(latestReport.value.id)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 30000)
  } finally {
    downloading.value = false
  }
}
</script>

<template>
  <div class="portal">
    <PortalHeader :customer-name="customerName"/>
    <CkAlert v-if="workOrderQuery.isError.value" tone="error" class="alert">Não foi possível carregar sua ordem de serviço.</CkAlert>
    <p v-else-if="workOrderQuery.isPending.value" class="status-message">Carregando…</p>
    <main v-else-if="activeWorkOrder" class="report">
      <template v-if="latestReport">
        <section class="report-head">
          <FileText/>
          <div><div class="ck-eyebrow">Relatório final · revisão {{ latestReport.revision }}</div><h1>{{ activeWorkOrder.vehicle.brand }} {{ activeWorkOrder.vehicle.model }} {{ activeWorkOrder.vehicle.year }}</h1></div>
        </section>
        <div class="report-body">
          <dl class="meta">
            <div><dt>Ordem de serviço</dt><dd class="ck-code">{{ activeWorkOrder.number }}</dd></div>
            <div><dt>Veículo</dt><dd>{{ activeWorkOrder.vehicle.brand }} {{ activeWorkOrder.vehicle.model }} {{ activeWorkOrder.vehicle.year }}</dd></div>
            <div><dt>Cliente</dt><dd>{{ customerName || 'Cliente' }}</dd></div>
            <div><dt>Placa</dt><dd class="ck-code">{{ activeWorkOrder.vehicle.plate }}</dd></div>
            <div><dt>Chassi (VIN)</dt><dd class="ck-code">{{ detailQuery.data.value?.vehicle.vin ?? '—' }}</dd></div>
            <div><dt>Entrada</dt><dd>{{ formatDateTime(detailQuery.data.value?.createdAt) }}</dd></div>
            <div><dt>Conclusão</dt><dd>{{ conclusao ? formatDateTime(conclusao) : 'Em andamento' }}</dd></div>
            <div><dt>Responsável técnico</dt><dd>{{ detailQuery.data.value?.technicianDisplayName ?? '—' }}</dd></div>
          </dl>

          <h2>Resumo da inspeção por etapa</h2>
          <CkTable
            v-if="checklistQuery.data.value?.length"
            :columns="inspectionColumns"
            :rows="checklistQuery.data.value"
            :row-key="(row) => row.id"
          >
            <template #default="{ item }">
              <span>{{ item.title }}</span>
              <span class="num ok">{{ inspectionCounts(item.items).ok }}</span>
              <span class="num warn">{{ inspectionCounts(item.items).warn }}</span>
              <span class="num fail">{{ inspectionCounts(item.items).fail }}</span>
            </template>
          </CkTable>

          <h2>Registro fotográfico</h2>
          <div v-if="mediaQuery.data.value?.length" class="photos">
            <div v-for="asset in mediaQuery.data.value" :key="asset.id" class="photo">
              <img :src="mediaUrls[asset.id]" :alt="asset.fileName">
              <span class="badge">{{ photoBadge[asset.phase] }}</span>
            </div>
          </div>
          <p v-else class="empty-inline">Nenhuma foto publicada ainda.</p>

          <div class="totals">
            <div><span>Peças e materiais</span><strong class="ck-code">{{ currency.format(partsTotal) }}</strong></div>
            <div><span>Mão de obra</span><strong class="ck-code">{{ currency.format(laborTotal) }}</strong></div>
            <div class="grand"><span>Total</span><strong class="ck-code">{{ currency.format(partsTotal + laborTotal) }}</strong></div>
          </div>

          <CkAlert v-if="approveError" tone="error">Não foi possível registrar a aprovação. Tente novamente.</CkAlert>

          <div class="signatures">
            <div class="signature">
              <div class="line">{{ accepted ? customerName || 'Cliente' : '' }}</div>
              <span>Cliente · {{ customerName || 'Cliente' }}</span>
              <small v-if="approval" class="ck-code approval-meta">Assinado em {{ formatDateTime(approval.acceptedAt) }} · hash {{ approval.documentSha256.slice(0, 12) }}…</small>
            </div>
            <div class="signature">
              <div class="line">{{ detailQuery.data.value?.technicianDisplayName ?? '' }}</div>
              <span>Responsável técnico · CK</span>
            </div>
          </div>

          <div class="report-actions">
            <CkButton variant="secondary" :disabled="downloading" @click="downloadPdf"><Download :size="16"/>{{ downloading ? 'Baixando…' : 'Baixar PDF' }}</CkButton>
            <div class="accept">
              <Check v-if="accepted"/><Clock v-else/>
              <p>{{ accepted ? 'Relatório aprovado e registrado.' : 'Ao aprovar, registraremos usuário, data, IP e hash desta versão.' }}</p>
              <CkButton :disabled="accepted || approving" @click="approveReport">{{ approving ? 'Enviando…' : (accepted ? 'Aprovado' : 'Aprovar relatório') }}</CkButton>
            </div>
          </div>
        </div>
      </template>
      <p v-else class="status-message">O relatório final ainda não foi solicitado para esta ordem de serviço.</p>
    </main>
    <p v-else class="status-message">Nenhuma ordem de serviço publicada para você no momento.</p>
  </div>
</template>

<style scoped>
.portal { min-height: 100vh; padding-bottom: 40px; }
.alert { margin: 24px max(24px, calc((100vw - 1100px) / 2)) 0; }
.status-message { padding: 32px max(24px, calc((100vw - 1100px) / 2)); color: var(--ck-text-muted); }
.report { max-width: 860px; margin: 28px auto; padding: 0 20px 40px; }
.report-head { display: flex; gap: 18px; padding: 30px; border-radius: var(--radius-lg) var(--radius-lg) 0 0; background: var(--ck-flame); color: white; }
.report-head .ck-eyebrow { color: white; }
.report-head h1 { margin: 5px 0; }
.report-body { padding: 32px; background: white; border-radius: 0 0 var(--radius-lg) var(--radius-lg); box-shadow: var(--shadow-lg); }
.meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding-bottom: 22px; border-bottom: 1.5px solid var(--ck-cream-300); }
.meta dt { font-size: 10px; text-transform: uppercase; letter-spacing: var(--ls-wide); color: var(--ck-text-faint); }
.meta dd { margin: 3px 0 0; font-size: 13px; }
h2 { margin: 26px 0 12px; font-size: 15px; text-transform: uppercase; letter-spacing: var(--ls-wide); color: var(--ck-maroon-700); }
.num { text-align: center; font-family: var(--font-mono); font-weight: 700; }
.num.ok { color: var(--ck-ok-500); }
.num.warn { color: var(--ck-warn-500); }
.num.fail { color: var(--ck-fail-500); }
.photos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.photo { position: relative; border-radius: var(--radius-md); overflow: hidden; background: var(--ck-ink-800); height: 90px; }
.photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.photo .badge { position: absolute; top: 6px; left: 6px; background: rgba(0,0,0,.55); color: var(--ck-amber-400); font-family: var(--font-mono); font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 5px; }
.empty-inline { color: var(--ck-text-muted); font-size: 13px; }
.totals { margin: 24px 0; padding: 16px 20px; border-radius: var(--radius-md); background: var(--ck-cream-100); }
.totals > div { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13.5px; color: var(--ck-text-muted); }
.totals .grand { padding-top: 10px; margin-top: 6px; border-top: 1.5px solid var(--ck-cream-300); font-family: var(--font-display); font-weight: 800; font-size: 18px; color: var(--ck-text); }
.totals .grand strong { color: var(--ck-maroon-700); }
.signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 28px; }
.signature .line { min-height: 34px; border-bottom: 1.5px solid var(--ck-text); display: flex; align-items: flex-end; padding-bottom: 4px; font-family: var(--font-serif); font-style: italic; font-size: 22px; color: var(--ck-maroon-700); }
.signature span { display: block; margin-top: 6px; color: var(--ck-text-muted); font-size: 12px; }
.approval-meta { display: block; margin-top: 3px; color: var(--ck-text-faint); font-size: 10.5px; }
.report-actions { margin-top: 28px; display: flex; flex-direction: column; gap: 14px; }
.accept { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; padding: 18px; border-radius: var(--radius-lg); background: var(--ck-cream-100); }
@media (max-width: 700px) {
  .meta { grid-template-columns: repeat(2, 1fr); }
  .photos { grid-template-columns: repeat(2, 1fr); }
  .signatures { grid-template-columns: 1fr; }
  .accept { grid-template-columns: auto 1fr; }
  .accept button { grid-column: 1/-1; }
}
</style>
